import { useTranslation } from 'react-i18next';

import type { StatusMessageProps } from '@/types';

import DisruptIcon from '@/assets/icons/disrupt.svg?react';
import EmptyFileIcon from '@/assets/icons/empty-file.svg?react';
import ErrorIcon from '@/assets/icons/error.svg?react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const StatusMessage = ({
  type,
  title,
  description,
  showReloadButton = false,
  onRetry,
  buttonText,
  className,
}: StatusMessageProps) => {
  const { t } = useTranslation();

  const config = {
    error: {
      icon: <ErrorIcon className="w-9.75 h-9.75 text-destructive" />,
      bgColor: 'bg-destructive-subtle',
    },
    empty: {
      icon: <EmptyFileIcon className="w-9.75 h-9.75 fill-muted-foreground opacity-50" />,
      bgColor: 'bg-accent',
    },
    disrupt: {
      icon: <DisruptIcon className="opacity-50" />,
      bgColor: 'bg-accent',
    },
  };

  const { icon, bgColor } = config[type];

  return (
    <div className={cn('flex flex-col gap-4 w-full items-center text-center', className)}>
      <div className={cn('flex items-center justify-center rounded-full w-25 h-25', bgColor)}>
        {icon}
      </div>

      <div className="flex flex-col gap-2 w-full max-w-80">
        <h3 className="font-semibold text-base text-foreground wrap-break-word">{title}</h3>
        <p className="text-sm text-muted-foreground wrap-break-word">{description}</p>
      </div>
      {showReloadButton && (
        <Button
          onClick={onRetry}
          className="inline-flex items-center justify-center h-9 px-4 rounded-lg border b-border text-foreground shadow-sm text-sm font-medium bg-background cursor-pointer"
        >
          {buttonText ?? t('common.reload')}
        </Button>
      )}
    </div>
  );
};

export default StatusMessage;
